<template>
  <dialog ref="dialogRef" class="app-modal" :aria-label="label"
          @cancel.prevent="requestClose" @close="handleClose" @click.stop>
    <Transition name="app-modal-fade" :duration="duration" @after-leave="closeDialog">
      <div v-if="modelValue" :style="{transitionDuration: duration + 'ms'}">
        <slot/>
      </div>
    </Transition>
  </dialog>
</template>

<script setup lang="ts">
import {onBeforeUnmount, onMounted, ref, watch} from "vue";

const props = withDefaults(defineProps<{
  modelValue: boolean;
  label: string;
  duration?: number;
}>(), {duration: 150});
const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void;
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);

// The browser's top layer escapes clipping without leaving the current shadow tree.
const openDialog = () => {
  const dialog = dialogRef.value;
  if (props.modelValue && dialog && !dialog.open) dialog.showModal();
};

const closeDialog = () => {
  if (!props.modelValue) dialogRef.value?.close();
};

const requestClose = () => emit('update:modelValue', false);
const handleClose = () => {
  if (!dialogRef.value?.open && props.modelValue) requestClose();
};

onMounted(openDialog);
watch(() => props.modelValue, openDialog, {flush: 'post'});
onBeforeUnmount(() => dialogRef.value?.close());
</script>

<style scoped>
.app-modal {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  overflow: auto;
}

.app-modal::backdrop {
  background: transparent;
}

.app-modal-fade-enter-active,
.app-modal-fade-leave-active {
  transition-property: opacity;
  transition-timing-function: ease;
}

.app-modal-fade-enter-from,
.app-modal-fade-leave-to {
  opacity: 0;
}
</style>